import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  Clock,
  Users,
  TrendingUp,
  Zap,
  CheckCircle2,
  Cog,
  BarChart3,
  MessageSquare,
  RefreshCw,
  Target,
  Lightbulb,
  Workflow,
  Bot
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 }
  }
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="relative py-24 overflow-hidden" data-testid="consulting-hero">
      <div className="absolute inset-0 bg-[#0E2A47]" />
      <div className="absolute inset-0 grid-overlay" />
      <div className="absolute inset-0 spotlight" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="mb-6">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-[#0FA4A9]/10 border border-[#0FA4A9]/30 text-[#0FA4A9] font-body text-sm font-medium">
                <Cog className="h-4 w-4" />
                AI & Automation Services
              </span>
            </motion.div>
            
            <motion.h1 
              variants={fadeInUp}
              className="font-heading text-4xl sm:text-5xl font-bold text-white leading-tight mb-6"
            >
              Stop Wasting Time on{' '}
              <span className="text-[#E53935]">Broken Workflows</span>
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              className="font-body text-lg text-white/70 mb-8"
            >
              We help businesses identify bottlenecks, implement AI solutions, and build automations that save time, win more customers, and deliver better service.
            </motion.p>

            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button 
                  data-testid="consulting-cta-btn"
                  className="bg-[#0FA4A9] hover:bg-[#0FA4A9]/90 text-white font-semibold uppercase tracking-wide px-8 py-6 rounded-sm text-base btn-shine transition-all duration-300 hover:shadow-[0_0_30px_-5px_rgba(15,164,169,0.6)]"
                >
                  Get a Free Consultation
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </motion.div>
          </motion.div>

          {/* Visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="hidden lg:block"
          >
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_fix-workflows/artifacts/d9lgxvbk_Chamber%20AI%20picture.jpg"
                alt="B.J. at CHQ Chamber Business Builder Workshop"
                className="rounded-sm border border-white/10"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Three Outcomes Section
const OutcomesSection = () => {
  const outcomes = [
    {
      icon: Clock,
      title: "Save Time",
      description: "Eliminate repetitive tasks, automate data entry, and free your team to focus on high-value work that actually moves the needle.",
      benefits: [
        "Automate manual data entry",
        "Streamline approval workflows",
        "Auto-generate reports and updates",
        "Reduce meeting overload"
      ],
      color: "#0FA4A9"
    },
    {
      icon: Users,
      title: "Get More Customers",
      description: "Never miss a lead again. Automated follow-ups, smart lead scoring, and AI-powered outreach that converts.",
      benefits: [
        "Instant lead response systems",
        "AI-powered lead qualification",
        "Automated nurture sequences",
        "Smart CRM integrations"
      ],
      color: "#0FA4A9"
    },
    {
      icon: TrendingUp,
      title: "Serve Customers Better",
      description: "Deliver faster responses, personalized experiences, and proactive support that keeps customers coming back.",
      benefits: [
        "24/7 AI-assisted support",
        "Personalized communications",
        "Proactive issue detection",
        "Faster resolution times"
      ],
      color: "#0FA4A9"
    }
  ];

  return (
    <section className="py-24 bg-[#0A1F35]" data-testid="outcomes-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.h2 
            variants={fadeInUp}
            className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4"
          >
            Three Ways We Transform Your Business
          </motion.h2>
          <motion.p 
            variants={fadeInUp}
            className="font-body text-lg text-white/60 max-w-2xl mx-auto"
          >
            Every engagement focuses on tangible outcomes that impact your bottom line
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
          className="grid lg:grid-cols-3 gap-8"
        >
          {outcomes.map((outcome, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="glass-card rounded-sm p-8 hover:border-[#0FA4A9]/50 transition-all duration-300 group"
            >
              <div className="w-16 h-16 rounded-sm bg-[#0FA4A9]/10 flex items-center justify-center mb-6 group-hover:bg-[#0FA4A9]/20 transition-colors">
                <outcome.icon className="h-8 w-8 text-[#0FA4A9]" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-white mb-4">
                {outcome.title}
              </h3>
              <p className="font-body text-white/60 mb-6">
                {outcome.description}
              </p>
              <ul className="space-y-3">
                {outcome.benefits.map((benefit, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[#0FA4A9] mt-0.5 flex-shrink-0" />
                    <span className="font-body text-white/70">{benefit}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// What We Do Section
const WhatWeDoSection = () => {
  const services = [
    {
      icon: Workflow,
      title: "Workflow Automation",
      description: "We map your existing processes, identify bottlenecks, and build custom automations that run 24/7 without human intervention."
    },
    {
      icon: Bot,
      title: "AI Integration",
      description: "From chatbots to document processing, we integrate AI tools that handle complex tasks and make smart decisions."
    },
    {
      icon: RefreshCw,
      title: "System Connections",
      description: "We connect your tools—CRM, email, spreadsheets, databases—so data flows seamlessly without manual copy-paste."
    },
    {
      icon: BarChart3,
      title: "Analytics & Insights",
      description: "Get dashboards that show what's working, what's not, and where to focus next for maximum impact."
    },
    {
      icon: MessageSquare,
      title: "AI-Powered Communication",
      description: "Automated email responses, smart follow-ups, and personalized messaging at scale."
    },
    {
      icon: Lightbulb,
      title: "Strategy & Training",
      description: "We don't just build—we teach your team to maintain, improve, and expand your AI capabilities."
    }
  ];

  return (
    <section className="py-24 bg-[#0E2A47]" data-testid="what-we-do-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.h2 
            variants={fadeInUp}
            className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4"
          >
            What We Build For You
          </motion.h2>
          <motion.p 
            variants={fadeInUp}
            className="font-body text-lg text-white/60 max-w-2xl mx-auto"
          >
            Practical solutions, not theoretical frameworks
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="glass-card rounded-sm p-6 hover:border-[#0FA4A9]/50 transition-colors tracing-beam"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-sm bg-[#0FA4A9]/10 flex items-center justify-center">
                  <service.icon className="h-6 w-6 text-[#0FA4A9]" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-white mb-2">
                    {service.title}
                  </h3>
                  <p className="font-body text-sm text-white/60">
                    {service.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Process Section
const ProcessSection = () => {
  const steps = [
    {
      number: "01",
      title: "Discovery Call",
      description: "We learn about your business, current workflows, and biggest pain points. No sales pitch—just understanding."
    },
    {
      number: "02",
      title: "Workflow Audit",
      description: "We map your processes, identify the leaks, and prioritize fixes by impact and feasibility."
    },
    {
      number: "03",
      title: "Solution Design",
      description: "We present a clear plan with specific automations, expected outcomes, and timeline."
    },
    {
      number: "04",
      title: "Build & Implement",
      description: "We build your solutions, test thoroughly, and deploy with minimal disruption to your team."
    },
    {
      number: "05",
      title: "Train & Support",
      description: "Your team learns to use and maintain the new systems. We're available for questions and optimizations."
    }
  ];

  return (
    <section className="py-24 bg-[#0A1F35]" data-testid="consulting-process-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.h2 
            variants={fadeInUp}
            className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4"
          >
            How We Work Together
          </motion.h2>
          <motion.p 
            variants={fadeInUp}
            className="font-body text-lg text-white/60 max-w-2xl mx-auto"
          >
            A clear, collaborative process from first call to working solution
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
          className="relative"
        >
          {/* Timeline Line */}
          <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-[#0FA4A9]/20" />

          <div className="space-y-12">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className={`flex flex-col lg:flex-row items-center gap-8 ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                }`}
              >
                <div className={`flex-1 ${index % 2 === 0 ? 'lg:text-right' : 'lg:text-left'}`}>
                  <div className="glass-card rounded-sm p-6 inline-block">
                    <h3 className="font-heading text-xl font-bold text-white mb-2">
                      {step.title}
                    </h3>
                    <p className="font-body text-white/60">
                      {step.description}
                    </p>
                  </div>
                </div>

                <div className="flex-shrink-0 w-16 h-16 rounded-full bg-[#0FA4A9] flex items-center justify-center z-10">
                  <span className="font-heading text-lg font-bold text-white">{step.number}</span>
                </div>

                <div className="flex-1 hidden lg:block" />
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  return (
    <section className="py-24 bg-[#0A1F35]" data-testid="consulting-cta-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.h2 
            variants={fadeInUp}
            className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6"
          >
            Ready to Fix Your Workflows?
          </motion.h2>
          <motion.p 
            variants={fadeInUp}
            className="font-body text-lg text-white/60 mb-10"
          >
            Book a free consultation call. We'll discuss your biggest challenges and show you exactly how we can help.
          </motion.p>
          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button 
                data-testid="consulting-final-cta-btn"
                className="bg-[#E53935] hover:bg-[#E53935]/90 text-white font-bold uppercase tracking-widest px-10 py-6 rounded-sm text-lg btn-shine transition-all duration-300 hover:shadow-[0_0_30px_-5px_rgba(229,57,53,0.6)]"
              >
                Book Free Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default function AIConsultingPage() {
  return (
    <div data-testid="ai-consulting-page">
      <HeroSection />
      <OutcomesSection />
      <WhatWeDoSection />
      <ProcessSection />
      <CTASection />
    </div>
  );
}
